import React, { useMemo, useState } from "react";
import { Button, Chip, TableSortLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { COLORS } from "../../utils/colors";
import { formatProductTypeLabel } from "../../utils/redemptionDuplicateGuardUi";

const INTERVAL_LABELS = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
  year: "Yearly"
};

const formatPaymentInterval = (product) => {
  if (!product?.interval) return "—";
  return INTERVAL_LABELS[product.interval] || product.interval;
};

const formatType = (type) => {
  if (!type) return "";
  return `${type[0].toUpperCase()}${type.slice(1)}`;
};

const formatDuration = (product) => {
  if (product.type !== "subscription" || product.subscriptionDuration == null || product.subscriptionDuration === "") {
    return "";
  }
  const months = parseInt(product.subscriptionDuration, 10);
  return `${product.subscriptionDuration} ${months === 1 ? "Month" : "Months"}`;
};

const SORT_COLUMNS = [
  { id: "name", label: "Product Name" },
  { id: "type", label: "Type" },
  { id: "duration", label: "Duration" },
  { id: "interval", label: "Payment Interval" },
  { id: "price", label: "Price" },
  { id: "rrp", label: "RRP" },
  { id: "productType", label: "Product type" }
];

const getSortValue = (product, column) => {
  switch (column) {
    case "name":
      return product.name || "";
    case "type":
      return product.type || "";
    case "duration":
      return product.type === "subscription" ? Number(product.subscriptionDuration) || 0 : -1;
    case "interval":
      return product.interval || "";
    case "price":
      return Number(product.price) || 0;
    case "rrp":
      return Number(product.rrp) || 0;
    case "productType":
      return formatProductTypeLabel(product);
    default:
      return "";
  }
};

const ProductList = ({ products }) => {
  const navigate = useNavigate();
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(column);
    setOrder("asc");
  };

  const sortedProducts = useMemo(() => {
    const rows = [...(products || [])];
    rows.sort((a, b) => {
      const aValue = getSortValue(a, orderBy);
      const bValue = getSortValue(b, orderBy);
      let result = 0;
      if (typeof aValue === "number" && typeof bValue === "number") {
        result = aValue - bValue;
      } else {
        result = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: "base" });
      }
      return order === "asc" ? result : -result;
    });
    return rows;
  }, [products, orderBy, order]);

  return (
    <TableContainer
      sx={{
        width: "fit-content",
        maxWidth: "calc(100vw - 200px)",
        margin: "0 auto",
        overflowX: "auto",
        "&::-webkit-scrollbar": {
          height: "8px"
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "4px"
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "4px",
          "&:hover": {
            background: "#555"
          }
        }
      }}
    >
      <Table
        sx={{ minWidth: 650, borderCollapse: "separate", "td, th": { borderBottom: "none" } }}
        aria-label="simple table"
      >
        <TableHead>
          <TableRow>
            {SORT_COLUMNS.map((column) => (
              <TableCell key={column.id} sortDirection={orderBy === column.id ? order : false}>
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : "asc"}
                  onClick={() => handleSort(column.id)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedProducts.map((product) => (
            <TableRow
              className={!product.active ? "border-2 border-[#ff0000] opacity-80" : ""}
              onClick={() => navigate(`/product/${product._id}`)}
              key={product._id}
              sx={{
                boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
                "&:hover": { cursor: "pointer" }
              }}
            >
              <TableCell component="th" scope="row">
                {product.name} {!product.active && <Chip variant="outlined" color="error" label="Inactive" />}
              </TableCell>
              <TableCell>{formatType(product.type)}</TableCell>
              <TableCell>{formatDuration(product)}</TableCell>
              <TableCell>{formatPaymentInterval(product)}</TableCell>
              <TableCell>{product.price}</TableCell>
              <TableCell>{product.rrp}</TableCell>
              <TableCell>{formatProductTypeLabel(product)}</TableCell>
              <TableCell>
                <Button sx={{ backgroundColor: COLORS.priorityBlue.hex }} variant="contained">
                  View Product
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductList;
